import { isEmpty, isNil } from 'lodash';
import React, { useContext } from 'react';
import { Options as RRuleOptions } from 'rrule';
import { View } from '@shared/components/View';
import { MBClearButton } from '@shared/components/button';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextBody2Inactive, MBTextOverline } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { RepeatText } from './RepeatText';

export function RepeatTextButton({
  onPress,
  rruleOptions,
  isConfiguring,
  onClear,
  disabled,
  offset,
  tagFilter,
  workflowTemplateId,
}: {
  onPress: () => void;
  isConfiguring: boolean;
  disabled: boolean;
  rruleOptions?: Partial<RRuleOptions>;
  onClear: () => void;
  offset?: MBAPI.CreateRecurrenceOffset | null;
  tagFilter?: MBAPI.FilterByComponent;
  workflowTemplateId?: string;
}) {
  const colors = useContext(ColorThemeContext);
  const hasRruleOptions = !isNil(rruleOptions);
  const isDesktop = useIsDesktop();
  const showClearButton = !isNil(onClear) && hasRruleOptions;

  // Validate offset is fully configured before showing as repeat config
  const hasValidTagFilter =
    !isNil(tagFilter?.componentId) && !isEmpty(tagFilter?.value?.containsString?.values);
  const hasValidOffset = !isNil(offset?.unit) && !isNil(offset?.value) && offset.value > 0;
  const isValidOneAtATime = hasValidTagFilter && hasValidOffset;

  const hasRepeatConfig = hasRruleOptions || isValidOneAtATime;

  return (
    <MBTouchableOpacity onPress={onPress} disabled={disabled}>
      <View
        row
        centerVertically
        paddingLeft={isDesktop ? 1.5 : 2}
        paddingRight={1}
        paddingBottom={1}
        style={{
          minHeight: 60,
          backgroundColor: 'transparent',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <View marginRight={1.5}>
          <MBIconV2 name="repeat" size={35} color={colors.accent} />
        </View>
        {!hasRepeatConfig && !isConfiguring && (
          <MBTextBody1 color={colors.foregroundActive}>
            {i18n('workflowTemplate.component.dateAndRepeat.addRepeat')}
          </MBTextBody1>
        )}
        {hasRepeatConfig && (
          <View flex paddingVertical={1}>
            {!isDesktop && (
              <MBTextOverline color={colors.foregroundInactive}>
                {i18n('workflowTemplate.component.dateAndRepeat.repeat')}
              </MBTextOverline>
            )}
            <RepeatText
              rruleOptions={rruleOptions}
              offset={offset}
              tagFilter={tagFilter}
              workflowTemplateId={workflowTemplateId}
            />
          </View>
        )}
        {showClearButton && <MBClearButton onPress={onClear} />}
        {isConfiguring && !hasRepeatConfig && (
          <MBTextBody2Inactive color={colors.foregroundInactive}>
            {i18n('workflowTemplate.component.dateAndRepeat.customRepeat')}
          </MBTextBody2Inactive>
        )}
      </View>
    </MBTouchableOpacity>
  );
}
